import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { actualizarActivo } from "../../utils/activos";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import SelectorFechaDocumento from "../SelectorFechaDocumento";

// Edita la fecha de proyección de pago (activos.fecha_planificada) de un CapEx planificado,
// directamente desde el panel "Movimientos Futuros" (hasta ahora solo se podía desde el modal
// Inversión del edificio). Escribe SOLO fecha_planificada — no toca fecha_compra ni el estado,
// así que no dispara depreciación ni saca el activo de la proyección: solo lo reubica en el tiempo.
//
// B.3 Commit 4 (2026-09-26, Fase 5 "Futuros: reagendar", camino #4): MIGRADO a modo ESTRICTO —
// el modal ya NO valida fecha por su cuenta (ni piso ni día hábil): el calendario compartido
// (SelectorFechaDocumento) deshabilita visualmente lo mismo que el writer (actualizarActivo) rechaza
// (10.1, mismo criterio que la celda del panel Futuros). El candado de "activo ya ejecutado sin plan"
// vive en la BASE (activos_bloquea_fecha_planificada_trg, aplicado F2) — su error llega acá como
// cualquier otro rechazo del writer, visible en el toast del catch (10.177).

const normalizarFecha = (fechaStr) => {
  if (!fechaStr) return "";
  return fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
};

const formatMonto = (entry) => {
  const n = Math.abs(Number(entry?.importeEnMoneda || 0));
  const num = n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  const moneda = entry?.moneda_origen === "USD" ? "US$ " : "";
  return `${moneda}${num}`;
};

const ModificarFechaProyeccionCapexModal = ({ show, onHide, entry, onSaved, pais }) => {
  const [fecha, setFecha] = useState(normalizarFecha(entry?.fecha_display));
  const [guardando, setGuardando] = useState(false);

  if (!entry) return null;

  const handleGuardar = async () => {
    if (!fecha) {
      toast.error("La fecha es obligatoria");
      return;
    }
    setGuardando(true);
    try {
      // Modo ESTRICTO fijo (camino #4, migrado): piso hoyDelPais(pais) + día hábil + candado de
      // "activo ya ejecutado sin plan" (BASE, activos_bloquea_fecha_planificada_trg) — cualquier
      // rechazo llega acá como Error y se muestra en el catch de abajo, nunca en silencio (10.177).
      await actualizarActivo(entry.activo_id, { fecha_planificada: fecha }, { modo: MODOS_VALIDACION.ESTRICTO, pais });
      toast.success("Fecha de proyección actualizada");
      // Patch puntual (Fase 2A): el padre actualiza `capexEntries` por activo_id, sin refetch.
      onSaved?.(fecha);
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Modificar fecha de pago</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 16 }}>
          <div>
            <strong>Concepto:</strong> {entry.razon_social_receptor || "—"}
          </div>
          <div>
            <strong>Monto:</strong> {formatMonto(entry)}
          </div>
        </div>

        <Form.Group>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Nueva fecha de pago</Form.Label>
          <SelectorFechaDocumento
            value={fecha}
            onChange={(nuevaFecha) => setFecha(nuevaFecha || "")}
            pais={pais}
            modo={MODOS_VALIDACION.ESTRICTO}
            disabled={guardando}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModificarFechaProyeccionCapexModal;
